import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QrScanner from 'qr-scanner';
import { API_BASE_URL } from '../config';
import { toast } from 'react-toastify';
import PodiumAnimation from './PodiumAnimation';

const AdminPanel = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTasksLocked, setIsTasksLocked] = useState(false);
  const [isGameEnded, setIsGameEnded] = useState(false);
  const [showPodium, setShowPodium] = useState(false);
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  const handleQrScan = (scannedCode) => {
    if (scannedCode) {
      scannerRef.current?.destroy();
      setIsScanning(false);
      
      axios.post(`${API_BASE_URL}/admin/verify`, {
        adminCode: scannedCode.trim()
      })
      .then(response => {
        if (response.data.success) {
          setIsAuthenticated(true);
          toast.success('Zalogowano jako administrator');
        } else {
          toast.error('Nieprawidłowy kod administratora');
        }
      })
      .catch(error => {
        console.error('Błąd weryfikacji administratora:', error);
        toast.error('Błąd weryfikacji administratora');
      });
    }
  };

  const startScanning = () => {
    setIsScanning(true);
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
    setIsScanning(false);
  };

  useEffect(() => {
    if (isScanning && videoRef.current) {
      scannerRef.current = new QrScanner(
        videoRef.current,
        result => handleQrScan(result.data),
        {
          preferredCamera: 'environment',
          highlightScanRegion: true,
        }
      );
      
      scannerRef.current.start();
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, [isScanning]);

  const toggleTasksLock = () => {
    axios.post(`${API_BASE_URL}/admin/toggle-tasks-lock`)
      .then(response => {
        setIsTasksLocked(response.data.tasksLocked);
        toast.success(response.data.tasksLocked ? 
          'Zadania zostały zablokowane dla wszystkich użytkowników' : 
          'Zadania zostały odblokowane dla wszystkich użytkowników'
        );
      })
      .catch(error => {
        toast.error('Wystąpił błąd podczas zmiany stanu zadań');
      });
  };

  const toggleGameEnd = () => {
    axios.post(`${API_BASE_URL}/admin/toggle-game-end`)
      .then(response => {
        setIsGameEnded(response.data.gameEnded);
        toast.success(response.data.gameEnded ? 
          'Gra została zakończona dla wszystkich użytkowników' : 
          'Gra została wznowiona'
        );
      })
      .catch(error => {
        toast.error('Wystąpił błąd podczas zmiany stanu gry');
      });
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 text-white">Panel Administratora</h2>
          <p className="mb-4 text-gray-300">Zeskanuj kod QR administratora aby uzyskać dostęp.</p>
          <button
            onClick={startScanning}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Skanuj kod administratora
          </button>
          
          {isScanning && (
            <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
              <div className="bg-gray-800 p-6 rounded-lg max-w-sm w-full mx-4 border border-gray-700">
                <h3 className="text-lg font-bold mb-4 text-white">Zeskanuj kod administratora</h3>
                <video ref={videoRef} className="w-full aspect-square object-cover rounded-lg" />
                <button
                  onClick={stopScanning}
                  className="mt-4 w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Anuluj
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      {showPodium && <PodiumAnimation />}
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-700">
        <h2 className="text-2xl font-bold mb-6 text-white">Panel Administratora</h2>
        <div className="space-y-4">
          <button
            onClick={() => setShowPodium(true)}
            className="w-full px-4 py-3 rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            Podium
          </button>
          <button
            onClick={toggleTasksLock}
            className={`w-full px-4 py-3 rounded-lg text-white transition-colors ${
              isTasksLocked 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isTasksLocked ? 'Odblokuj zadania' : 'Zablokuj zadania'}
          </button>
          
          <button
            onClick={toggleGameEnd}
            className={`w-full px-4 py-3 rounded-lg text-white transition-colors ${
              isGameEnded 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isGameEnded ? 'Wznów grę' : 'Zakończ grę'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
